<script setup lang="ts">
import { BsPersonCircle, BsCardList, BsFillHouseDoorFill, BsPeopleFill, BsBoxArrowRight, BsFillGearFill, BsSunglasses, BsPersonFillAdd } from "vue-icons-plus/bs";
import MenuBarEntry from "@/components/menu/MenuBarEntry.vue";
import { useRouter } from "vue-router";
import { computed, ref } from "vue";
import { useAuthStore } from "@/stores/auth.ts";

const router = useRouter();
const authStore = useAuthStore();
const accountMenu = ref<boolean>(false);

const isAdminRoute = computed(() => {
  const current_route = router.currentRoute.value.name;
  return current_route && current_route.toString().startsWith('admin');
});

export type MenuBarRoute = {
  name: string
  action?: (() => unknown)
  icon: any
  label: string
  modules?: string[]
}

const generalRoutes: MenuBarRoute[] = [
  {
    name: 'account',
    action: () => { accountMenu.value = !accountMenu.value },
    icon: BsPersonCircle,
    label: 'Account',
    modules: []
  }
]

const userRoutes: MenuBarRoute[] = [
  {
    name: 'home',
    icon: BsFillHouseDoorFill,
    label: 'Home'
  },
  {
    name: 'games',
    icon: BsCardList,
    label: 'Spiele'
  },
  {
    name: 'groups',
    icon: BsPeopleFill,
    label: 'Gruppen'
  }
]

const adminRoutes: MenuBarRoute[] = [
  {
    name: 'admin.dashboard',
    icon: BsFillHouseDoorFill,
    label: 'Dashboard'
  },
  {
    name: 'admin.games',
    icon: BsCardList,
    label: 'Spiele',
    modules: ['GAME_PLAN']
  },
  {
    name: 'admin.games.results',
    icon: BsCardList,
    label: 'Spielergebnisse',
    modules: ['GAME_RESULT']
  },
  {
    name: 'admin.groups',
    icon: BsPeopleFill,
    label: 'Gruppen',
    modules: ['GAME_PLAN']
  },
  {
    name: 'admin.teams',
    icon: BsPersonFillAdd,
    label: 'Teams',
    modules: ['GAME_PLAN']
  }
]

const routes = computed(() => {
  let routes: MenuBarRoute[] = [];

  if (authStore.account === null || !isAdminRoute.value) {
    routes = [...userRoutes, ...generalRoutes];
  } else {
    routes = [...adminRoutes, ...generalRoutes];
  }

  return routes.filter(route => {
    if (!route.modules) {
      return true;
    }
    return authStore.hasAllModules(route.modules);
  });
});

async function onLogoutClick() {
  await authStore.logout()
  await router.push({ name: 'home' });
  accountMenu.value = false;
}

</script>

<template>
  <div class="menu-bar-w">
    <div class="menu-bar">
      <MenuBarEntry
        v-for="route in routes"
        :key="route.name"
        :action="route.action"
        :name="route.name"
        :icon="route.icon"
        :label="route.label"
      />
    </div>
    <div class="account-menu" v-if="authStore.account && accountMenu">
      <router-link v-if="isAdminRoute" class="account-menu-item" :to="{ name: 'home' }" @click="accountMenu = false">
        <BsSunglasses />
        <span>Nutzeransicht</span>
      </router-link>
      <router-link v-else class="account-menu-item" :to="{ name: 'admin.dashboard' }" @click="accountMenu = false">
        <BsFillGearFill />
        <span>Admin Bereich</span>
      </router-link>
      <button class="account-menu-item" @click.prevent="onLogoutClick">
        <BsBoxArrowRight />
        <span>Logout</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.menu-bar-w {
  position: relative;
  height: 100%;
  width: 100%;
  max-width: 600px;
}

.menu-bar {
  background-color: var(--color-layer-1);
  border-color: var(--color-layer-0);
  border-width: 0.25rem;
  border-top-style: solid;
  border-bottom: none;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
}

@media (min-width: 600px) {
  .menu-bar {
    border-radius: 1rem 1rem 0 0;
    border-left-style: solid;
    border-right-style: solid;
  }
}

.account-menu {
  position: absolute;
  display: flex;
  flex-direction: column;
  bottom: 100%;
  right: 0;
  background-color: var(--color-layer-1);
  border: 0.25rem solid var(--color-layer-0);
  border-radius: 1rem;
  overflow: hidden;

  .account-menu-item {
    all: unset;
    box-sizing: border-box;
    cursor: pointer;
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--text-color-layer-1);
    text-decoration: none;
    font-size: 1rem;

    &:hover {
      background-color: var(--color-layer-2);
    }
  }
}
</style>
